import { useState, useEffect } from "react";
import { CloudLightning, Search, Wind, Droplets, AlertTriangle, FileText, Play, Download, ChevronRight, MapPin, Loader2, Bell, Shield } from "lucide-react";
import { Button } from "./ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback static data for when API is unavailable
const FALLBACK_STORM_DATA = [
  {
    date: "2024-11-15",
    type: "Severe Thunderstorm",
    affected_zips: ["20745", "20746", "20747", "20748", "20744"],
    wind_speed: "65 mph",
    hail_size: "1 inch",
    description: "Strong storms moved through southern Prince George's County with damaging winds and hail.",
    region: "Prince George's County, MD"
  },
  {
    date: "2024-10-28",
    type: "Nor'easter",
    affected_zips: ["20001", "20002", "20003", "20004", "20005", "20006"],
    wind_speed: "55 mph",
    hail_size: "None",
    description: "Powerful nor'easter brought heavy rain and strong winds across the DC metro area.",
    region: "Washington DC"
  },
  {
    date: "2024-09-12",
    type: "Hail Storm",
    affected_zips: ["20814", "20815", "20816", "20817", "20818"],
    wind_speed: "45 mph",
    hail_size: "1.5 inch",
    description: "Large hail reported in Bethesda and surrounding areas causing roof damage.",
    region: "Montgomery County, MD"
  },
  {
    date: "2024-08-05",
    type: "Derecho",
    affected_zips: ["22101", "22102", "22180", "22181", "22182"],
    wind_speed: "80 mph",
    hail_size: "0.75 inch",
    description: "Fast-moving derecho caused widespread damage across Northern Virginia.",
    region: "Fairfax County, VA"
  },
  {
    date: "2024-07-18",
    type: "Severe Thunderstorm",
    affected_zips: ["21401", "21402", "21403", "21404", "21405"],
    wind_speed: "60 mph",
    hail_size: "1 inch",
    description: "Strong thunderstorms caused roof and tree damage in the Annapolis area.",
    region: "Anne Arundel County, MD"
  }
];

const DAMAGE_TYPES = [
  {
    title: "Wind Damage",
    icon: Wind,
    signs: [
      "Missing or lifted shingles",
      "Exposed roof deck",
      "Damaged flashing",
      "Loose gutters"
    ],
    color: "blue"
  },
  {
    title: "Hail Damage",
    icon: CloudLightning,
    signs: [
      "Dents in shingles",
      "Granule loss",
      "Cracked shingles",
      "Damaged vents"
    ],
    color: "purple"
  },
  {
    title: "Water Damage",
    icon: Droplets,
    signs: [
      "Water stains on ceiling",
      "Mold or mildew",
      "Peeling paint",
      "Sagging roof deck"
    ],
    color: "teal"
  }
];

const RESOURCES = [
  {
    title: "Homeowner's Guide to Storm Damage Claims",
    type: "PDF Guide",
    description: "Step-by-step guide to filing an insurance claim after storm damage."
  },
  {
    title: "Understanding Your Insurance Estimate",
    type: "PDF Guide",
    description: "Learn how to read and understand your insurance adjuster's estimate."
  },
  {
    title: "Roof Inspection Checklist",
    type: "Checklist",
    description: "What to look for when inspecting your roof after a storm."
  }
];

export default function StormCenterSection() {
  const [zipCode, setZipCode] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [locationInfo, setLocationInfo] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [dmvAlerts, setDmvAlerts] = useState([]);

  // Fetch DMV-wide alerts on mount
  useEffect(() => {
    const fetchDMVAlerts = async () => {
      try {
        const states = ['MD', 'VA', 'DC'];
        const allAlerts = [];
        
        for (const state of states) {
          try {
            const response = await fetch(`${API_URL}/api/storms/state-alerts/${state}`);
            if (response.ok) {
              const data = await response.json();
              if (data.alerts) {
                allAlerts.push(...data.alerts.map(a => ({ ...a, state })));
              }
            }
          } catch (e) {
            console.log(`Could not fetch alerts for ${state}`);
          }
        }
        
        setDmvAlerts(allAlerts.slice(0, 5)); // Show top 5 alerts
      } catch (e) {
        console.log('Could not fetch DMV alerts');
      }
    };
    
    fetchDMVAlerts();
  }, []);

  const handleSearch = async () => {
    if (!zipCode || zipCode.length < 5) return;
    
    setIsSearching(true);
    setError(null);
    setActiveAlerts([]);
    setLocationInfo(null);
    
    try {
      const response = await fetch(`${API_URL}/api/storms/alerts?zip_code=${zipCode}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch storm data');
      }
      
      const data = await response.json();
      
      setLocationInfo(data.location);
      setActiveAlerts(data.active_alerts || []);
      
      // Combine API historical data with fallback data
      const historicalFromAPI = (data.historical_storms || []).map(storm => ({
        date: storm.date,
        type: storm.type,
        description: storm.description,
        region: `${storm.county}, ${storm.state}`,
        wind_speed: storm.magnitude ? `${storm.magnitude} ${storm.magnitude_type}` : "N/A",
        hail_size: "N/A",
        fromAPI: true
      }));
      
      // If API returned historical data, use it; otherwise fall back to the generic
      // DMV storm list, but only when it is actually relevant to the searched ZIP
      // (matching on the 3-digit ZIP prefix used by affected_zips) instead of showing
      // unrelated regional storms for every ZIP in the country.
      if (historicalFromAPI.length > 0) {
        setSearchResults(historicalFromAPI);
      } else {
        const zipPrefix = zipCode.slice(0, 3);
        const relevantFallback = FALLBACK_STORM_DATA.filter(storm =>
          (storm.affected_zips || []).some(z => z.slice(0, 3) === zipPrefix)
        );
        setSearchResults(relevantFallback);
      }
      
    } catch (err) {
      console.error('Storm search error:', err);
      setError('Could not connect to weather service. Showing recent DMV storm history.');
      // Use fallback data
      setSearchResults(FALLBACK_STORM_DATA);
    } finally {
      setIsSearching(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'extreme': return 'bg-red-100 text-red-800 border-red-300';
      case 'severe': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'moderate': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  return (
    <section id="storm-center" className="py-20 md:py-28 bg-gradient-to-b from-amber-50 to-white" data-testid="storm-center-section">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-amber-200">
            <CloudLightning className="w-4 h-4" />
            Storm Center
          </div>
          <h2 className="font-heading text-4xl md:text-5xl text-slate-900 mb-4">
            Storm Damage Resources
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Search for recent storms in your area, learn about damage signs, 
            and get expert guidance on insurance claims.
          </p>
        </div>

        {/* Storm Search */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg mb-12">
          <h3 className="text-slate-900 font-heading text-2xl mb-6 text-center">Search Recent Storms by ZIP Code</h3>
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <div className="relative flex-grow">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="Enter ZIP Code"
                className="w-full h-14 pl-12 pr-4 rounded-xl bg-slate-50 text-slate-900 border border-slate-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 placeholder-slate-400 text-lg"
                data-testid="storm-zip-input"
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={zipCode.length < 5 || isSearching}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold h-14 px-8 rounded-xl"
              data-testid="storm-search-btn"
            >
              <Search className="w-5 h-5 mr-2" />
              Search
            </Button>
          </div>

          {/* Active DMV Alerts Banner */}
          {dmvAlerts.length > 0 && !searchResults && (
            <div className="mt-6 p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-5 h-5 text-red-600 animate-pulse" />
                <span className="text-red-800 font-semibold">Active Weather Alerts in DMV Area</span>
              </div>
              <div className="space-y-2">
                {dmvAlerts.map((alert, i) => (
                  <div key={i} className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{alert.event}</span>
                      <span className="text-xs">{alert.state}</span>
                    </div>
                    {alert.headline && (
                      <p className="text-sm mt-1 opacity-80">{alert.headline.slice(0, 150)}...</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search Results */}
          {isSearching && (
            <div className="mt-8 text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-3" />
              <p className="text-slate-600">Fetching real-time weather data...</p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-800 text-sm text-center">
              {error}
            </div>
          )}

          {searchResults !== null && !isSearching && (
            <div className="mt-8">
              {/* Location Info */}
              {locationInfo && (locationInfo.city || locationInfo.state) && (
                <div className="mb-4 text-center">
                  <p className="text-slate-600">
                    Results for <span className="font-semibold text-slate-900">
                      {locationInfo.city}{locationInfo.city && locationInfo.state ? ', ' : ''}{locationInfo.state}
                    </span>
                  </p>
                </div>
              )}

              {/* Active Alerts for this ZIP */}
              {activeAlerts.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 rounded-xl border-2 border-red-300">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="text-red-800 font-bold">
                      {activeAlerts.length} Active Weather Alert{activeAlerts.length > 1 ? 's' : ''} for Your Area!
                    </span>
                  </div>
                  <div className="space-y-3">
                    {activeAlerts.map((alert, i) => (
                      <div key={i} className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-lg">{alert.event}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(alert.severity)}`}>
                            {alert.severity}
                          </span>
                        </div>
                        {alert.headline && (
                          <p className="text-sm font-medium mb-2">{alert.headline}</p>
                        )}
                        {alert.description && (
                          <p className="text-sm opacity-80">{alert.description.slice(0, 300)}...</p>
                        )}
                        {alert.expires && (
                          <p className="text-xs mt-2 opacity-60">
                            Expires: {new Date(alert.expires).toLocaleString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => scrollToSection("booking")}
                    className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Schedule Emergency Roof Inspection
                  </Button>
                </div>
              )}

              <div className="space-y-4">
                <h4 className="text-slate-700 font-semibold text-center mb-4 bg-slate-100 py-2 rounded-lg">
                  {activeAlerts.length > 0 
                    ? "Recent Storm History in Your Region:"
                    : `Storm history for ZIP code ${zipCode}:`
                  }
                </h4>
                {searchResults.length === 0 && (
                  <p className="text-slate-500 text-sm text-center py-4">
                    No recorded storm history found for this ZIP code.
                  </p>
                )}
                {searchResults.map((storm, index) => (
                    <div 
                      key={index} 
                      className="rounded-xl p-5 border bg-amber-50 border-amber-200"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            {storm.fromAPI && (
                              <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full mr-1">LIVE DATA</span>
                            )}
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                            <span className="text-slate-900 font-semibold">{storm.type}</span>
                          </div>
                          <p className="text-slate-600 text-sm">{storm.description}</p>
                          <p className="text-slate-500 text-xs mt-1">Region: {storm.region}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-900 font-medium">
                            {storm.date ? new Date(storm.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent'}
                          </p>
                          {storm.wind_speed && storm.wind_speed !== 'N/A' && (
                            <p className="text-amber-600 text-sm">Wind: {storm.wind_speed}</p>
                          )}
                          {storm.hail_size && storm.hail_size !== 'N/A' && (
                            <p className="text-purple-600 text-sm">Hail: {storm.hail_size}</p>
                          )}
                        </div>
                      </div>
                    </div>
                ))}
                
                {/* Always show CTA */}
                <div className="text-center pt-4">
                  <p className="text-slate-600 text-sm mb-3">Even if your area wasn't directly hit, nearby storms can still cause damage.</p>
                  <Button
                    onClick={() => scrollToSection("booking")}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    Schedule a Free Roof Inspection
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Damage Types */}
        <div className="mb-16">
          <h3 className="text-slate-900 font-heading text-2xl mb-8 text-center">Recognize Storm Damage</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {DAMAGE_TYPES.map((damage, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  damage.color === 'blue' ? 'bg-blue-100' :
                  damage.color === 'purple' ? 'bg-purple-100' : 'bg-teal-100'
                }`}>
                  <damage.icon className={`w-6 h-6 ${
                    damage.color === 'blue' ? 'text-blue-600' :
                    damage.color === 'purple' ? 'text-purple-600' : 'text-teal-600'
                  }`} />
                </div>
                <h4 className="text-slate-900 font-semibold text-lg mb-3">{damage.title}</h4>
                <ul className="space-y-2">
                  {damage.signs.map((sign, i) => (
                    <li key={i} className="text-slate-600 text-sm flex items-start gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${
                        damage.color === 'blue' ? 'bg-blue-600' :
                        damage.color === 'purple' ? 'bg-purple-600' : 'bg-teal-600'
                      }`} />
                      {sign}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Resources */}
        <div className="mb-16">
          <h3 className="text-slate-900 font-heading text-2xl mb-8 text-center">Homeowner Resources</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {RESOURCES.map((resource, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all group cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                    <FileText className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <span className="text-emerald-600 text-xs font-medium uppercase tracking-wider">{resource.type}</span>
                    <h4 className="text-slate-900 font-semibold mt-1 mb-2">{resource.title}</h4>
                    <p className="text-slate-600 text-sm">{resource.description}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  className="mt-4 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 w-full justify-start px-0"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Guide
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Insurance Tips */}
        <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl p-8 border border-amber-200">
          <h3 className="text-slate-900 font-heading text-2xl mb-6 text-center">Insurance Claim Tips</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Document damage with photos and videos before making repairs",
              "File your claim as soon as possible after discovering damage",
              "Get multiple contractor estimates for comparison",
              "Don't sign any contracts before your claim is approved",
              "Keep all receipts for emergency repairs",
              "Request a copy of the adjuster's report"
            ].map((tip, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {index + 1}
                </span>
                <p className="text-slate-700">{tip}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              onClick={() => scrollToSection("upload-estimate")}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-5"
            >
              Upload Your Estimate for Review
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
